import { TaskId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Content, PageHeader, Stage } from "components/layout";
import { ErrorMessage } from "components/ui/error-message";
import { InlineBanner } from "components/ui/inline-banner";
import { useGetTaskQuery, useGetUserTenantRegionOptionsQuery } from "generated/graphql";
import React, { useMemo } from "react";
import { match, Redirect } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";

interface Props {
    match: match<{ taskId: string }>;
}

/**
 * Currently a component that serves as a link target.
 * Fetches the task and redirects to the task detail on the entity page.
 */
export const TaskDetail: React.FC<Props> = props => {
    const { match } = props;
    const regionId = useRegionId();
    const regionsQuery = useGetUserTenantRegionOptionsQuery({}, stableQueryOptions());
    const hasMultipleRegionsAvailable = useMemo(() => {
        if (regionsQuery.isLoading || !regionsQuery.data) {
            return false;
        }
        if (regionsQuery.data.getUserTenantRegionOptions.options.length <= 1) {
            return false;
        }
        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionsQuery, regionsQuery.data]);
    const query = useGetTaskQuery({ regionId, taskId: match.params.taskId as TaskId });
    return (
        <QueryRenderer name="TaskDetail" query={query}>
            {data =>
                data.getTask.populatedTask ? (
                    <Redirect to={`${data.getTask.populatedTask.entity.path}/tasks`} />
                ) : (
                    <Stage>
                        <PageHeader title="Tasks" />
                        <Content>
                            {hasMultipleRegionsAvailable ? (
                                <InlineBanner type="warning" fontStyle="normal">
                                    This task is not available in the current office.
                                    Please select a different office to view this task.
                                </InlineBanner>
                            ) : (
                                <ErrorMessage component="TaskDetail">
                                    Sorry, this task could not be found.
                                </ErrorMessage>
                            )}
                        </Content>
                    </Stage>
                )
            }
        </QueryRenderer>
    );
};
